import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase.js";
import { callFunction } from "../lib/callFunction.js";
import { useAuth } from "../lib/auth.js";
import { Badge, Button, Card, EmptyState, Modal } from "../lib/ui.js";

/** The same limits the database enforces (packages/shared/rules/rules.ts, migration 0030). */
const MAX_RULES = 40;
const MAX_RULE_CHARS = 400;
const MAX_TOTAL_CHARS = 6000;
const MAX_INSTRUCTION_CHARS = 2000;

interface RuleRow {
  id: string;
  fact: string;
  status: "proposed" | "active";
  op: "add" | "update" | "delete";
  target_rule_id: string | null;
  source_call_id: string | null;
  created_at: string;
}

interface ExtractResult {
  proposals: Array<{ id: string }>;
  notRules: Array<{ text: string; reason: string }>;
}

function ruleErrorMessage(message: string): string {
  if (message.includes("rule_budget_exceeded")) return `That would go over the limit (${MAX_RULES} rules, ${MAX_TOTAL_CHARS} characters in all). Remove or shorten a rule first.`;
  if (message.includes("rule_text_invalid")) return `A rule needs to be between 8 and ${MAX_RULE_CHARS} characters.`;
  if (message.includes("rule_not_pending")) return "That one was already dealt with.";
  if (message.includes("rule_not_found")) return "That rule is no longer active.";
  if (message.includes("not_authorized")) return "You don't have access to change that.";
  return message || "That couldn't be completed.";
}

/**
 * Receptionist rules: plain-words instructions that change how the receptionist handles calls, without anyone
 * writing code. Staff type what it should do differently; it is turned into short rules (supabase/functions/
 * receptionist-rule-extract) that wait here for approval; approved rules apply to every call that starts after.
 * Rules can refine how the receptionist works but never override its fixed safeguards.
 */
export function Rules() {
  const { activeBusinessId } = useAuth();
  const [params, setParams] = useSearchParams();
  const callId = params.get("call");

  const [rows, setRows] = useState<RuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [instruction, setInstruction] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notRules, setNotRules] = useState<Array<{ text: string; reason: string }>>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<RuleRow | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeBusinessId) return;
    const { data } = await supabase
      .from("receptionist_rules")
      .select("id, fact, status, op, target_rule_id, source_call_id, created_at")
      .eq("business_id", activeBusinessId)
      .in("status", ["proposed", "active"])
      .order("created_at");
    setRows((data ?? []) as RuleRow[]);
    setLoading(false);
  }, [activeBusinessId]);

  useEffect(() => {
    void load();
  }, [load]);

  const proposals = rows.filter((r) => r.status === "proposed");
  const active = rows.filter((r) => r.status === "active");
  const activeChars = active.reduce((n, r) => n + r.fact.length, 0);
  const activeById = new Map(active.map((r) => [r.id, r]));

  async function submit() {
    if (!activeBusinessId || !instruction.trim()) return;
    setWorking(true);
    setError(null);
    setNotRules([]);
    const res = await callFunction<ExtractResult>("receptionist-rule-extract", {
      business_id: activeBusinessId,
      instruction: instruction.trim(),
      ...(callId ? { call_id: callId } : {}),
    });
    setWorking(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setNotRules(res.data.notRules ?? []);
    if ((res.data.proposals ?? []).length > 0) setInstruction("");
    await load();
  }

  async function review(row: RuleRow, approve: boolean) {
    setBusyId(row.id);
    setError(null);
    const edited = drafts[row.id];
    const { error: err } = await supabase.rpc("rpc_portal_review_rule", {
      p_rule_id: row.id,
      p_approve: approve,
      p_fact: approve && edited !== undefined && edited.trim() !== row.fact ? edited : null,
    });
    setBusyId(null);
    if (err) setError(ruleErrorMessage(err.message));
    await load();
  }

  async function saveEdit(row: RuleRow) {
    const text = drafts[row.id];
    if (text === undefined || text.trim() === row.fact) {
      setEditingId(null);
      return;
    }
    setBusyId(row.id);
    setError(null);
    const { error: err } = await supabase.rpc("rpc_portal_edit_rule", { p_rule_id: row.id, p_fact: text });
    setBusyId(null);
    if (err) {
      setError(ruleErrorMessage(err.message));
      return;
    }
    setEditingId(null);
    await load();
  }

  async function remove(row: RuleRow) {
    setBusyId(row.id);
    const { error: err } = await supabase.rpc("rpc_portal_retire_rule", { p_rule_id: row.id });
    setBusyId(null);
    setRemoveTarget(null);
    if (err) setError(ruleErrorMessage(err.message));
    await load();
  }

  if (loading) return <p style={{ color: "var(--rc-text-muted)" }}>Loading...</p>;

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "var(--rc-text)" }}>Receptionist rules</h1>
      <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: "4px 0 20px" }}>
        Tell the receptionist what it should do differently, in your own words. It turns that into short rules for you to approve. Approved rules apply to
        calls that start after you approve them. Rules refine how it works; they can't change its fixed safeguards, like never cancelling or moving a booking
        the caller didn't ask about.
      </p>

      <Card style={{ marginBottom: 20 }}>
        {callId && (
          <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 10px" }}>
            Based on a call you opened.{" "}
            <button
              onClick={() => setParams({})}
              style={{ background: "none", border: "none", padding: 0, color: "var(--rc-link)", cursor: "pointer", fontSize: 12.5 }}
            >
              Not from a call
            </button>
          </p>
        )}
        <label htmlFor="rule-instruction" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--rc-text)" }}>
          What should the receptionist do differently?
        </label>
        <textarea
          id="rule-instruction"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value.slice(0, MAX_INSTRUCTION_CHARS))}
          rows={4}
          placeholder={
            callId
              ? "For example: when someone asks to move their booking to a different person, it should move the booking, not make a new one."
              : "For example: always ask for the caller's last name when booking."
          }
          disabled={working}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: 10,
            fontSize: 14,
            fontFamily: "inherit",
            border: "1px solid var(--rc-border-strong)",
            borderRadius: "var(--rc-radius-md)",
            background: "var(--rc-surface)",
            color: "var(--rc-text)",
            resize: "vertical",
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, gap: 12 }}>
          <span style={{ fontSize: 12, color: "var(--rc-text-faint)" }}>
            {active.length} of {MAX_RULES} rules in use
          </span>
          <Button variant="primary" onClick={submit} disabled={working || !instruction.trim()}>
            {working ? "Working it out..." : "Turn into rules"}
          </Button>
        </div>
        {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "10px 0 0" }}>{error}</p>}
        {notRules.length > 0 && (
          <div style={{ marginTop: 12, background: "var(--rc-warning-soft)", border: "1px solid var(--rc-warning)", borderRadius: 8, padding: 12, fontSize: 13 }}>
            <strong>Not turned into a rule:</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {notRules.map((n, i) => (
                <li key={i}>{n.reason}</li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {proposals.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 15, margin: "0 0 4px", color: "var(--rc-text)" }}>Waiting for your approval</h2>
          <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 10px" }}>Check each one says what you meant. You can change the wording before approving.</p>
          {proposals.map((p) => {
            const target = p.target_rule_id ? activeById.get(p.target_rule_id) : undefined;
            const wording = drafts[p.id] ?? p.fact;
            return (
              <Card key={p.id} style={{ marginBottom: 10 }}>
                <div style={{ marginBottom: 8 }}>
                  <Badge tone={p.op === "delete" ? "danger" : p.op === "update" ? "warning" : "primary"}>
                    {p.op === "add" ? "New rule" : p.op === "update" ? "Change a rule" : "Remove a rule"}
                  </Badge>
                </div>
                {p.op === "update" && target && <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 6px" }}>Now: {target.fact}</p>}
                {p.op === "delete" ? (
                  <p style={{ fontSize: 14, margin: "0 0 10px", color: "var(--rc-text)" }}>{target?.fact ?? p.fact}</p>
                ) : (
                  <textarea
                    aria-label="Rule wording"
                    value={wording}
                    onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value.slice(0, MAX_RULE_CHARS) }))}
                    rows={2}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: 8,
                      fontSize: 14,
                      fontFamily: "inherit",
                      border: "1px solid var(--rc-border-strong)",
                      borderRadius: "var(--rc-radius-md)",
                      background: "var(--rc-surface)",
                      color: "var(--rc-text)",
                      marginBottom: 10,
                    }}
                  />
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <Button variant={p.op === "delete" ? "danger" : "primary"} onClick={() => review(p, true)} disabled={busyId === p.id}>
                    {p.op === "delete" ? "Remove it" : "Approve"}
                  </Button>
                  <Button variant="secondary" onClick={() => review(p, false)} disabled={busyId === p.id}>
                    Reject
                  </Button>
                </div>
              </Card>
            );
          })}
        </section>
      )}

      <section>
        <h2 style={{ fontSize: 15, margin: "0 0 10px", color: "var(--rc-text)" }}>
          Rules in use{" "}
          <span style={{ fontWeight: 400, fontSize: 12.5, color: "var(--rc-text-faint)" }}>
            ({active.length} of {MAX_RULES}, {activeChars} of {MAX_TOTAL_CHARS} characters)
          </span>
        </h2>
        {active.length === 0 ? (
          <EmptyState>No rules yet. The receptionist is using its standard behaviour.</EmptyState>
        ) : (
          active.map((r) => (
            <Card key={r.id} style={{ marginBottom: 8 }}>
              {editingId === r.id ? (
                <>
                  <textarea
                    aria-label="Rule wording"
                    value={drafts[r.id] ?? r.fact}
                    onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value.slice(0, MAX_RULE_CHARS) }))}
                    rows={2}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: 8,
                      fontSize: 14,
                      fontFamily: "inherit",
                      border: "1px solid var(--rc-border-strong)",
                      borderRadius: "var(--rc-radius-md)",
                      background: "var(--rc-surface)",
                      color: "var(--rc-text)",
                      marginBottom: 8,
                    }}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button variant="primary" onClick={() => saveEdit(r)} disabled={busyId === r.id}>
                      Save
                    </Button>
                    <Button variant="secondary" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                  <p style={{ margin: 0, fontSize: 14, color: "var(--rc-text)" }}>{r.fact}</p>
                  <div style={{ display: "flex", gap: 10, whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => {
                        setDrafts((d) => ({ ...d, [r.id]: r.fact }));
                        setEditingId(r.id);
                      }}
                      style={{ background: "none", border: "none", padding: 0, color: "var(--rc-link)", cursor: "pointer", fontSize: 13 }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setRemoveTarget(r)}
                      style={{ background: "none", border: "none", padding: 0, color: "var(--rc-danger)", cursor: "pointer", fontSize: 13 }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </section>

      {removeTarget && (
        <Modal title="Remove this rule?" onClose={() => setRemoveTarget(null)} width={420}>
          <p style={{ fontSize: 13.5, color: "var(--rc-text)", marginTop: 0 }}>{removeTarget.fact}</p>
          <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)" }}>The receptionist stops following it on the next call.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button variant="secondary" onClick={() => setRemoveTarget(null)}>
              Keep it
            </Button>
            <Button variant="danger" onClick={() => remove(removeTarget)} disabled={busyId === removeTarget.id}>
              Remove rule
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
